/**
 * VoicemailLineItem.js
 * Talkroute
 *
 * Created by Vel Genov on 2/13/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Grid } from "semantic-ui-react";

import { updateReadStatus } from "../../actions/voicemailActions";
import { getDescriptionForId } from "../../services/dataClasses/VoicemailMailbox";
import ReadIndicator from "./ReadIndicator";
import { domClasses } from "@constants/testConstants";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import { createStyles, makeStyles } from "@material-ui/core";
import clsx from "clsx";

const useStyles = makeStyles(
  createStyles({
    hoverOnStyle: { opacity: "0.6", backgroundColor: "var(--color-white)" },
    hoverOffStyle: { opacity: "1.0", backgroundColor: "var(--color-white)" },
    selectedStyle: { backgroundColor: "var(--color-light-grey)" },
    selectedLightStyle: { backgroundColor: "var(--color-very-light-grey)" },
    removeVerticalPadding: { paddingTop: "0px", paddingBottom: "0px" },
    gridStyles: {
      cursor: "pointer",
      margin: "0px",
      borderBottom: "1px solid var(--color-dark-grey)",
      userSelect: "none"
    },
    itemDescription: {
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    mailboxDescription: {
      lineHeight: "1em",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    lastColPadding: {
      paddingTop: "20px",
      paddingBottom: "20px",
    },
  })
);

interface IVoicemailLineItemProps {
  voicemailId: number;
  duration: number;
  mailboxId: string;
  messageRead: boolean;
  date: string | null;
  phoneNumber: string;
  isSelected?: boolean;
  darkColoredSelection?: boolean;
  showDetailsCallback: (voicemailId: number) => any;
  handleClick: (event: any) => any;
  handleContextMenu: (event: any) => any;
}

/**
 * A comoponent that displays a single voicemail line item with the passed in props.
 * This will usually be displayead as a part of a collection
 *
 * @param voicemail the voicemail to display
 * @param showDetailsCallback a callback function to show voicemail details when this line item is selected
 */
const VoicemailLineItem = ({
  voicemailId,
  mailboxId,
  messageRead,
  duration,
  date,
  phoneNumber,
  showDetailsCallback,
  handleClick,
  handleContextMenu,
  isSelected,
  darkColoredSelection
}: IVoicemailLineItemProps) => {
  const mailboxes = useAppSelector((state) => state.voicemails.mailboxes);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [hover, setHover] = useState(false);

  /**
   * Toggle the read indicator for the current line item
   */
  const toggleReadIndicator = () => {
    markVoicemailAsRead(!messageRead);
  };

  /**
   * Mark the voicemail as read
   *
   * @param isRead is an optional boolean parameter that indicates wheather
   * the voicemail is read or unread. Read by default
   */
  const markVoicemailAsRead = (isRead = true) => {
    if (isRead !== messageRead) {
      // Create an update action
      dispatch(
        updateReadStatus({
          voicemailId,
          readStatus: isRead,
        })
      );
    }
  };

  /**
   * Called when the user is hovering over a VoicemailLineItem
   */
  const hoverOn = () => setHover(true);

  /**
   * Called when the user is moving out of a VoicemailLineItem
   */
  const hoverOff = () => setHover(false);

  const mailboxDescription = getDescriptionForId(mailboxId, mailboxes);

  // Determine the rendering style based on hover status
  const selectionColor = darkColoredSelection ? classes.selectedStyle : classes.selectedLightStyle
  const hoverStyle = hover ? classes.hoverOnStyle : classes.hoverOffStyle;
  const gridStyle = isSelected ? selectionColor : hoverStyle;

  return (
    <div 
    onClick={handleClick}
    onContextMenu={handleContextMenu}
    >
      <Grid
        onMouseEnter={() => hoverOn()}
        onMouseLeave={() => hoverOff()}
        className={clsx(classes.gridStyles, gridStyle)}
        style={{
          margin: "0px",
        }}
      >
        <Grid.Column
          width={2}
          textAlign="center"
          verticalAlign="middle"
          className={classes.removeVerticalPadding}
        >
          <ReadIndicator
            messageRead={messageRead}
            toggleCallBack={toggleReadIndicator}
          />
        </Grid.Column>
        <Grid.Column
          className={domClasses.VOICEMAIL_LINE_ITEM_DESCRIPTION}
          width={9}
          onClick={() => showDetailsCallback(voicemailId)}
        >
          <Grid.Row className={clsx(classes.itemDescription, "text-normal")}>
            {phoneNumber}
          </Grid.Row>
          <Grid.Row
            className={clsx(classes.mailboxDescription, "text-small-light")}
          >
            {mailboxDescription}
          </Grid.Row>
        </Grid.Column>
        <Grid.Column
          width={5}
          textAlign="right"
          onClick={() => showDetailsCallback(voicemailId)}
          className={clsx(classes.lastColPadding, "text-small")}
        >
          {date}
        </Grid.Column>
      </Grid>
    </div>
  );
};

export default VoicemailLineItem;
